/**
 * @file NumRec Reducer.
 */
import uniqBy from 'lodash.uniqby';

import {
  NUMREC_FETCH_SELECTED_IDENTITY_REQUEST,
  NUMREC_FETCH_SELECTED_IDENTITY_SUCCESS,
  NUMREC_VOTES_CAST_REQUEST,
  NUMREC_VOTES_CAST_SUCCESS,
  NUMREC_VOTES_CAST_FAILURE,
  NUMREC_SELECT_PHOTOS,
  NUMREC_DESELECT_PHOTOS,
  NUMREC_IDENTITY_DONE_REQUEST,
  NUMREC_IDENTITY_DONE_SUCCESS,
  NUMREC_IDENTITY_DONE_FAILURE,
} from '../actions/num-rec-actions';
import {
  QUEUE_EXTEND_LEASES,
  QUEUE_DROP_LEASES,
} from '../actions/queue-actions';
import { extendLeaseBatch } from '../../utilities/leasing';

const initialState = {
  queue: [],
  loading: false,
  error: null,
  castingVotes: false,
  markingIdentityAsDone: false,
};

/**
 * Helper factory for creating vote batches for a numrec queue. This is to
 * ensure consistency and provide data schema clarity.
 *
 * @param {Object} batch - The batch.
 * @returns {{leaseId: string, expiresAt: number, selectedIdentity: Object, selectedNumRecPhotos: Array<Object>}}
 */
const createVoteBatch = batch => ({
  // All batches have this info.
  leaseId: batch.leaseId,
  expiresAt: batch.expiresAt,
  selectedIdentity: batch.selectedIdentity,
  selectedNumRecPhotos: [],
});

const numRecReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case NUMREC_FETCH_SELECTED_IDENTITY_REQUEST:
      return {
        ...state,
        loading: true,
      };

    case NUMREC_FETCH_SELECTED_IDENTITY_SUCCESS: {
      const lease = createVoteBatch({
        leaseId: action.lease.lease_id,
        expiresAt: action.lease.expiresAt,
        selectedIdentity: action.lease.selectedIdentity,
      });

      return {
        ...state,
        loading: false,
        queue: [lease, ...state.queue],
      };
    }

    case NUMREC_SELECT_PHOTOS: {
      const { numRecPhotos } = action;
      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        selectedNumRecPhotos: uniqBy(
          [...numRecPhotos, ...batch.selectedNumRecPhotos].filter(
            numRecPhoto => !!numRecPhoto.face,
          ),
          numRecPhoto => numRecPhoto?.face?.id,
        ),
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case NUMREC_DESELECT_PHOTOS: {
      const { numRecPhotos } = action;
      const numRecFacesIds = numRecPhotos
        .filter(numRecPhoto => !!numRecPhoto.face)
        .map(numRecPhoto => numRecPhoto?.face?.id);
      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        selectedNumRecPhotos: batch.selectedNumRecPhotos.filter(
          selectedNumRecPhoto =>
            !numRecFacesIds.includes(selectedNumRecPhoto?.face?.id),
        ),
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case NUMREC_VOTES_CAST_REQUEST: {
      return {
        ...state,
        castingVotes: true,
      };
    }

    case NUMREC_VOTES_CAST_SUCCESS: {
      const { votes = [] } = action;

      const numRecFacesIds = votes.map(vote => vote?.face_id);
      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        selectedNumRecPhotos: batch.selectedNumRecPhotos.filter(
          selectedNumRecPhoto =>
            !numRecFacesIds.includes(selectedNumRecPhoto?.face?.id),
        ),
      };

      return {
        ...state,
        castingVotes: false,
        queue: [updatedBatch, ...rest],
      };
    }

    case NUMREC_VOTES_CAST_FAILURE: {
      return {
        ...state,
        castingVotes: false,
        error: action.error,
      };
    }

    case NUMREC_IDENTITY_DONE_REQUEST: {
      return {
        ...state,
        markingIdentityAsDone: true,
      };
    }

    case NUMREC_IDENTITY_DONE_SUCCESS: {
      const [, ...rest] = state.queue;

      return {
        ...state,
        markingIdentityAsDone: false,
        queue: rest,
      };
    }

    case NUMREC_IDENTITY_DONE_FAILURE: {
      return {
        ...state,
        markingIdentityAsDone: false,
        error: action.error,
      };
    }

    case QUEUE_EXTEND_LEASES: {
      return {
        ...state,
        queue: state.queue.map(batch => extendLeaseBatch(batch, action)),
      };
    }

    case QUEUE_DROP_LEASES: {
      return {
        ...state,
        queue: state.queue.filter(
          batch => !action.leaseIds.includes(batch.leaseId),
        ),
      };
    }

    default:
      return state;
  }
};

export { initialState };
export default numRecReducer;
